import { Dayjs } from "dayjs"
import {
  ClientFragment,
  EditScheduledNudgeMutationVariables,
  EditScheduledQuestionnaireMutationVariables,
  Enum_Insight_Content_Type_Enum,
  Enum_Outcome_Enum,
  Enum_Question_Type_Enum,
  Enum_Trend_Direction_Enum,
  Maybe,
  NudgeFragment,
  NudgeScheduleFragment,
  OrganizationFragment,
  QuestionnaireFragment,
  QuestionnaireItemFragment,
  QuestionnaireScheduleFragment,
  QuestionnaireTakesSinceDateByUserIdQuery,
  ScheduleNudgeMutationVariables,
  ScheduleQuestionnaireMutationVariables,
} from "../generated/graphql"

import { NudgeDomain } from "./constants"
import { DAYS_OF_WEEK } from "./helpers"

export enum GRAPHQL_ERROR_CODES {
  INVALID_JWT = "invalid-jwt",
}

export interface MoodMeasurement {
  id: number
  enjoyment: number
  growth?: number | null | undefined
  cravings?: number | null
  created_on: string
  client_created_on?: string | null
}

export interface Session {
  jwt: string
  userId: string
  role: string
}

export interface ErrorResponse {
  error: string
  message: string
  statusCode: number
}

export interface LoginResponse {
  user: {
    id: string
    default_role: string
    allowed_roles: string[]
  }
  jwt_expires_in: number
  jwt_token: string
}

export interface SleepTrend {
  bedtimeOffsetHour: number
  waketimeOffsetHour: number
  sleepDurationMinutes: number
  featureDate: string
}

export interface Organization {
  id: number
  name: string
  location: string
}

export interface User {
  id: string
  email?: string
  fullName?: string | null
  firstName?: string | null
  lastName?: string | null
  isOrgAdmin: boolean
  organizations: Organization[]
}

export interface MoodBarChartData {
  day: string
  moodLevel: number
  fill: string
  label?: number
  createdOn?: string
}

export interface MoodBarChartProps {
  data?: {
    date: Dayjs
    enjoyment?: number
    growth?: number | null | undefined
  }[]
}

export interface MoodBarDataProps {
  data?: MoodMeasurement[]
  timePeriod: string
  setTimePeriod?: React.Dispatch<React.SetStateAction<any>>
}

export interface PositionProps {
  x?: number
  y?: number
}

export interface FeatureReportFeatures {
  message: string
  percentStr: string
  percent: number
}

export interface FeatureReport {
  createdOn: string
  displayableOn: string
  outcome: Enum_Outcome_Enum
  features: FeatureReportFeatures[]
}

export interface InsightsProps {
  checkIns: number
  client: ClientFragment
  growthReport?: FeatureReport
  enjoyReport?: FeatureReport
  mvp2?: boolean
}

export interface CheckinDataProps {
  checkIns: number
  fill: string
  report?: FeatureReport
}

export interface TopFactorsProps {
  lines: boolean
  fill: string
  features: FeatureReportFeatures[]
}

export interface MoodIconProps extends PositionProps {
  backgroundColor?: string
  height?: number
  width?: number
}

export interface TrendData {
  label: string
  value: string
  unit?: "min" | "am" | "pm"
  direction: Enum_Trend_Direction_Enum
}

export interface Trends {
  walk: TrendData
  exercise: TrendData
  stationary: TrendData
  driving: TrendData
  bedtime: TrendData
  waketime: TrendData
  timeInBed: TrendData
  morningness: TrendData
  positiveWords: TrendData
  negativeWords: TrendData
  selfFocusWords: TrendData
  rigidWords: TrendData
  activeSocialMedia: TrendData
  timeAtHome: TrendData
}

export interface ChartDataPoints {
  x: string
  y: number
  label?: string
  unit?: string
  name?: string
  date?: string
  darkColor?: string
  lightColor?: string
  fill?: string
  stroke?: string
  other?: string | number // overloaded to support both trend & assessment charts
}

export type ChartLines = number[]

export interface Activity {
  walking: ChartDataPoints[][]
  exercise: ChartDataPoints[][]
  stationary: ChartDataPoints[][]
  driving: ChartDataPoints[][]
}

export interface Sleep {
  x: string
  y: number
  y0: number
  date: string
  label?: string
  bedtime: number
  waketime: number
}

export interface Language {
  positive: ChartDataPoints[][]
  negative: ChartDataPoints[][]
  selfFocus: ChartDataPoints[][]
  rigid: ChartDataPoints[][]
}

export type ScheduleItem = NudgeScheduleFragment | QuestionnaireScheduleFragment
export type UnscheduledItem = NudgeFragment | QuestionnaireFragment

export const STORAGE = {
  JWT: "jwt",
  ROLE: "role",
  USER_ID: "userId",
} as const

export type Order = "asc" | "desc"

export interface DataItem {
  sortData: any
  data?: any
  align?: "right" | "left" | "center"
}

export interface ICasemix {
  low: number
  moderate: number
  high: number
  noData: number
  total: number
}

export interface StaffTableData {
  id: DataItem
  firstName: DataItem
  lastName: DataItem
  viraClients: DataItem
  nudges: DataItem
  questionnaires: DataItem
  casemix: DataItem
  more: DataItem
}

export interface TakeCounts {
  nudgeTakes: number
  questionnaireTakes: number
}

export interface StaffOverviewData {
  viraClients: number
  nudges: number
  questionnaires: number
}
export interface TechniqueProfileData {
  topTenTechniques: {
    label: string
    domain: NudgeDomain
    percent: number
    active?: boolean
  }[]
  all?: {
    label: string
    domain: NudgeDomain
    percent: number
    active?: boolean
  }[]
  mostUsed: {
    label: string
    domain: NudgeDomain
    count: number
    active?: boolean
  }[]
  leastUsed: {
    label: string
    domain: NudgeDomain
    count: number
    active?: boolean
  }[]
}

export type ClientProgressData = { x: string; y: number }[][]

/**
 * The data for the main table on the My Clients page
 */
export interface ClientTableData {
  id: DataItem
  firstName: DataItem
  lastName: DataItem
  displayName: DataItem
  dailyCheckins: DataItem
  mobileSensingIndex: DataItem
  questionnaires: DataItem
  more: DataItem
}

export enum TechniquesAndInterventionType {
  nudge,
  questionnaire,
}

export interface TechniquesAndInterventions {
  id: number
  domain?: string
  technique?: string
  type: TechniquesAndInterventionType
  default_messaging: string
  in_app_content_id?: number
  organization: OrganizationFragment
  name: String
  questionnaire_items?: QuestionnaireItemFragment[]
}

export interface TechniquesAndInterventionsTableData {
  id: DataItem
  domain: DataItem
  technique: DataItem
  type: DataItem
  active: DataItem
  default_messaging: DataItem
  resource_id: DataItem
  questionnaire_items: DataItem
  created_by: DataItem
  more: DataItem
}

export interface UserInsightsTableData {
  id: DataItem
  direction: DataItem
  outcome: DataItem
  date: DataItem
  feature: DataItem
  insight: DataItem
  display_text: DataItem
}

export interface UserInsightsHowTableData {
  id: DataItem
  string: DataItem
}
export interface UserInsightsQuestionTableData {
  id: DataItem
  string: DataItem
}

export interface UserInsightsResourceTableData {
  id: DataItem
  author: DataItem
  content_type: DataItem
  content: DataItem
  public: DataItem
  owner_org_id: DataItem
  title: DataItem
  more: DataItem
}

export interface InAppResourcesTableData {
  id: DataItem
  more: DataItem
  author: DataItem
  content: DataItem
  content_type: DataItem
  created_by: DataItem
  created_on: DataItem
  owner_org_id: DataItem
  public: DataItem
  title: DataItem
  updated_on: DataItem
  header_image: DataItem
}

export interface TechniqueAndInterventionFragment {
  id: number
  domain?: string
  technique?: string
  name?: string
  type: string
  default_messaging: string
  in_app_content_id?: number
  created_by?: string
  public?: boolean
  owner_org_id?: number
}

export type Resource = {
  id: number
  author?: Maybe<string>
  content: string
  created_by?: Maybe<any>
  created_on: Maybe<any>
  content_type: Enum_Insight_Content_Type_Enum
  owner_org_id?: Maybe<number>
  public?: Maybe<boolean>
  //resource_type: Enum_Resource_Type_Enum
  title?: Maybe<string>
  updated_on: Maybe<any>
  header_image?: Maybe<string>
}

export enum TIME_PERIOD {
  WEEK = "week",
  MONTH = "month",
  HISTORY = "history",
}

export enum ScheduleTab {
  NUDGE = "Nudge",
  ASSESSMENT = "Assessment",
}

export enum PickerTab {
  RECURRING = "Recurring",
  ONE_TIME = "One Time",
}

export type ScheduleMutationVariables =
  | ScheduleNudgeMutationVariables
  | ScheduleQuestionnaireMutationVariables
  | EditScheduledNudgeMutationVariables
  | EditScheduledQuestionnaireMutationVariables

export type RecurringDays<
  T extends keyof Omit<ScheduleMutationVariables, "recurring_start_date">
> = T extends `recurring_${infer Day}` ? `recurring_${Day}` : never

export type InitDaysValue = RecurringDays<
  keyof Omit<ScheduleMutationVariables, "recurring_start_date">
>
export interface InitDaysProps {
  value: InitDaysValue
  label: DAYS_OF_WEEK
  selected: boolean
}
export type QuestionnaireTakeByUser =
  QuestionnaireTakesSinceDateByUserIdQuery["questionnaire_takes"]
export interface QuestionnairesChartData {
  chartLength: number
  hasIncompleteQuestionnaires: boolean
  questionnaireTakes: QuestionnaireTakeByUser
}

export interface AssessmentTableData {
  completeAt: DataItem
  questionnaireName: DataItem
  displayScore: DataItem & {
    scoreType?: string
    containsRiskQuestions?: boolean
  }
  severityColor: DataItem
  severityTitle: DataItem
  completeStatus: DataItem
  questionnaireResponses: DataItem
}

export interface DailyDoseResponse {
  response: number
  response_date: string
}

export interface DailyDoseDataProps {
  data?: DailyDoseResponse[]
}

export interface DailyDoseChartProps {
  data?: {
    date: Dayjs
    distress?: number
  }[]
}

export type SafetyChecklistMetadata = {
  client_first_name: string
}

interface QuestionnaireItem {
  display_order: number
  question: string
  header?: string | null | undefined
}

interface QuestionnaireResponseOptions {
  response_text: string
  response_value: number
  triggers_risk_alert: boolean
}

export interface QuestionnaireResponseProps {
  questionnaire_item: QuestionnaireItem
  questionnaire_response_options: QuestionnaireResponseOptions
}

export interface VirasafeMetrics {
  user_id?: string
  n_day_streak?: number
  n_weekly_keep_me_calm?: number
  n_weekly_keep_me_safe?: number
  top_engaged_activity_1?: string | null
  top_engaged_activity_2?: string | null
  top_engaged_activity_3?: string | null
  top_engaged_activity_4?: string | null
  least_distressed_activity_1?: string | null
  least_distressed_activity_2?: string | null
  least_distressed_activity_3?: string | null
}
export interface QuestionProps {
  id: string
  pageId: string
  questionType: Enum_Question_Type_Enum
  displayOrder: number
  text?: string
  explanationText?: string
  properties?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface PageProps {
  id: string
  assessmentId: string
  displayOrder: number
  showCautionModal?: boolean
  createdAt?: string
  updatedAt?: string
}
export interface Assessment {
  id: string
  title: string
  subtitle: string
  version: number
  firstPageId: string
  assessmentLength: number
  createdAt?: string
  updatedAt?: string
  deletedAt?: string
}

export interface AssessmentTake {
  assessmentId: string
  results: QuestionResult[]
  takeId?: string
  startedAt?: string
  completedAt?: string
}

export interface QuestionResult {
  questionId: string
  customPromptId?: string | null
  result?:
    | string
    | number
    | boolean
    | SwitchBoardQuestionResult[]
    | SelectQuestionResult
}

export interface SelectQuestionResult {
  index: number
  label: string
}

export interface SwitchBoardQuestionResult {
  index: number
  value: boolean
}

export type PopulatedAssessmentPage = PageProps & {
  questions: QuestionProps[]
}

export interface PopulatedAssessment extends Assessment {
  pages: PopulatedAssessmentPage[]
}

export interface SwitchboardOption {
  title: string
  subtitle?: string
  index: number
}
